import { PREFERENCE_MAX, PREFERENCE_MIN, type PlanResponse, type RidingPreferences } from '../../shared/contracts.js';
import { formatDistance, formatDuration, formatExtraTime } from '../../shared/format.js';
import { cn } from '../lib/cn.js';
import { PREFERENCE_LABELS } from '../preferences.js';
import { Badge, Button, Panel } from './ui.js';

/**
 * The planned route's summary. The planner shows it in full; the share overview
 * shows only the headline figures (`compact`) for a clean screenshot.
 */
export function RouteResultCard({ plan, requested, eyebrow = 'Recommended route', compact = false, dirty = false, busy = false, onShare, onDebug, className }: {
  plan: PlanResponse;
  requested: RidingPreferences;
  eyebrow?: string;
  compact?: boolean;
  dirty?: boolean;
  busy?: boolean;
  onShare?: () => void;
  onDebug?: () => void;
  className?: string;
}) {
  const neutral = Object.values(plan.appliedPreferences).every((value) => value === PREFERENCE_MIN) && !plan.returnRoute?.applied;
  return (
    <Panel className={cn('result-card', className)} aria-live={compact ? undefined : 'polite'}>
      <div className="result-topline">
        <div><p className="eyebrow">{eyebrow}</p><h2>{plan.route.usesFerry ? 'Estimated travel time' : 'Estimated riding time'}</h2></div>
        {compact ? null : <Badge tone={dirty || plan.relaxed ? 'warning' : 'accent'}>{dirty ? 'Outdated' : neutral ? 'Neutral route' : plan.relaxed ? 'Preferences eased' : 'Within allowance'}</Badge>}
      </div>
      <div className="metric-grid">
        <div><strong>{formatDistance(plan.route.distanceMeters)}</strong><span>distance</span></div>
        <div><strong>{formatDuration(plan.route.timeSeconds)}</strong><span>{plan.route.usesFerry ? 'travel time' : 'riding time'}</span></div>
        <div><strong>{formatExtraTime(plan.extraSeconds)}</strong><span>versus neutral</span></div>
      </div>
      {plan.returnRoute ? <p className="return-route-note">{plan.returnRoute.message}</p> : null}
      {compact ? null : <>
        <p className="result-message">{plan.message}</p>
        {onShare ? <Button type="button" variant="outline" className="share-button" disabled={dirty || busy} onClick={onShare}>Share view</Button> : null}
        {onDebug ? <Button type="button" variant="quiet" className="debug-button" onClick={onDebug}>Route internals</Button> : null}
        {plan.relaxed && !dirty ? <ul className="applied-list">{PREFERENCE_LABELS.filter(({ key }) => requested[key] > PREFERENCE_MIN).map(({ key, title }) => <li key={key}><span>{title}</span><strong>{plan.appliedPreferences[key]}/{PREFERENCE_MAX} applied{plan.appliedPreferences[key] < requested[key] ? ` · asked ${requested[key]}` : ''}</strong></li>)}</ul> : null}
        {plan.warnings.length > 0 ? <div className="warning-list">{plan.warnings.map((warning) => <p key={warning}>ⓘ {warning}</p>)}</div> : null}
        <p className="coverage-note">{plan.coverage} · route data from OpenStreetMap · <span className="plan-id" title="Quote this id when reporting a problem with this route">Plan {plan.planId}</span></p>
      </>}
    </Panel>
  );
}
